import classnames from 'classnames';
import React, {
  useRef,
  forwardRef,
  KeyboardEvent,
  ForwardRefRenderFunction,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TeamsDiscoveryDrawer } from '@wetransfer/transfer/component/TeamsDiscoveryDrawer/TeamsDiscoveryDrawer';
import Downloader from '@wetransfer/transfer/component/transfer/downloader';
import TermsConditions from '@wetransfer/transfer/component/transfer/terms-conditions';
import TermsConditionsExperiment from '@wetransfer/transfer/component/transfer/terms-conditions/term-and-conditions-experiment';
import Uploader from '@wetransfer/transfer/component/transfer/uploader';
import { TransferCustomizationDrawer } from '@wetransfer/transfer/component/TransferCustomizationDrawer';
import { useTransferCustomization } from '@wetransfer/transfer/component/TransferCustomizationProvider/hooks';
import TeamDiscoveryExperiment from '@wetransfer/transfer/constant/experiments/team-discovery-experiment';
import { ENTER } from '@wetransfer/transfer/constant/keyboard-keys';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import {
  verifyUsersList,
  usersNotInTeam,
} from '@wetransfer/transfer/helper/team-discovery';
import {
  userHasPaidTier,
  userHasPremium2022Tier,
  userHasPro2022Tier,
} from '@wetransfer/transfer/helper/user';
import { useCurrentTeam } from '@wetransfer/transfer/hooks/useCurrentTeam/useCurrentTeam';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import I18n from '@wetransfer/transfer/modules/i18n';
import Actions from '@wetransfer/transfer/store/action';
import { selectIsConsentCookieOverlayActive } from '@wetransfer/transfer/store/selector/consent';
import { selectIsLayoutHidden } from '@wetransfer/transfer/store/selector/layout';

/*
  The Transfer component is actually one of the following components,
  but which one gets decided in this file.
  */
import { State } from '@wetransfer/transfer/store/types';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';

import {
  useClearTransferWindowNotifications,
  useOnboardingRedirect,
  useTransferMode,
  useRouteTransition,
} from './TransferWindow.hooks';
import { TransferWindowProps } from './TransferWindow.types';

export let drawerDisplayCondition = false;

const TransferWindow: ForwardRefRenderFunction<unknown, TransferWindowProps> =
  // We don't want to forward this ref to the componentProps
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ id, secret, route, recipient_id, ref, ...rest }) => {
    const transferId = Browser.isMobile ? route.params.id : id;
    const transferSecret = Browser.isMobile ? route.params.secret : secret;

    const recipientId = Browser.isMobile
      ? route.params.recipient_id
      : recipient_id;

    const dismissTeamsDrawerForeverCookie = useMemo(
      () => Cookie.get(AppConfig.cookies.dismissTeamsDrawerForever.name),
      []
    );

    const transferMode = useTransferMode({
      transferId,
      transferSecret,
    });

    const isCookieOverlayActive = useSelector(
      selectIsConsentCookieOverlayActive
    );

    const isLayoutHidden = useSelector(selectIsLayoutHidden);
    const dispatch = useDispatch();
    const transferWindowRef = useRef<HTMLDivElement>();

    const { user, transfer, isPanelOpen } = useSelector((state: State) => ({
      user: state.user,
      transfer: state.transfer,
      isPanelOpen: state.route.isPanelOpen,
    }));
    const routeTransition = useRouteTransition(route.type);

    const teamExperimentFlag =
      TeamDiscoveryExperiment.isShowTeamDiscoveryExperiment();

    useClearTransferWindowNotifications(route.type);
    useOnboardingRedirect(route.type);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const [isDrawerOpenForAnimation, setIsDrawerOpenForAnimation] =
      useState(false);
    const { currentTeam } = useCurrentTeam();

    const componentProps = {
      route,
      transferMode,
      ...rest,
      id: transferId,
      secret: transferSecret,
      recipient_id: recipientId,
    };

    function showTransfer() {
      dispatch(Actions.Layout.showUI());
    }

    function handleShowTransferKeyPress(e: KeyboardEvent) {
      const isKeyEnter = e.key === ENTER;
      e.preventDefault();

      if (isKeyEnter) {
        showTransfer();
      }
    }

    const isHalfPanelDesktop = !(
      routeTransition.fullPanel && !Browser.isMobile
    );

    const showUploader = transferMode === 'upload' && isHalfPanelDesktop;
    const showDownloader = transferMode === 'download' && isHalfPanelDesktop;
    const showTandc = transferMode === 'tandc' && isHalfPanelDesktop;

    const showTandcExp =
      transferMode === 'tandc-experiment' && isHalfPanelDesktop;

    //main conditions to display drawer. Some extra conditions will be added according to the state
    drawerDisplayCondition =
      teamExperimentFlag &&
      showUploader &&
      transfer.state === 'finished' &&
      transfer.type === 'email' &&
      !dismissTeamsDrawerForeverCookie &&
      !isPanelOpen &&
      userHasPaidTier(user) &&
      verifyUsersList(transfer.recipients, user.email, currentTeam);

    const [showDrawer, setShowDrawer] = useState(false);

    useEffect(() => {
      if (drawerDisplayCondition) {
        setIsDrawerOpen(true);
      }
    }, [transfer.state]);

    useEffect(() => {
      setShowDrawer(drawerDisplayCondition && isDrawerOpen === true);
    }, [isDrawerOpen]);

    useEffect(() => {
      setShowDrawer(drawerDisplayCondition && !isPanelOpen);
    }, [isPanelOpen, drawerDisplayCondition]);

    function hideDrawerIfViewportTooSmall() {
      const viewportWidth = window.innerWidth;

      setShowDrawer(
        drawerDisplayCondition && isDrawerOpen && viewportWidth > 1040
      );
    }

    window.addEventListener('resize', hideDrawerIfViewportTooSmall);

    function textContentParameter() {
      if (userHasPro2022Tier(user)) {
        return 'Pro';
      } else if (userHasPremium2022Tier(user)) {
        return 'Premium';
      }
    }

    const customization = useTransferCustomization();

    const isTransferCustomizationDrawerEnabled =
      customization.isEnabledForSender &&
      transfer.state === TRANSFER_STATES.INIT;

    return (
      <div
        data-testid="transferWindow"
        className={classnames('transfer', {
          ['transfer--html5-fullscreen']: isLayoutHidden,
          ['transfer__cookie-wall-active']: isCookieOverlayActive,
          ['transfer--full-panel']: routeTransition.fullPanel,
          ['transfer--half-panel']: routeTransition.halfPanel,
          ['transfer--drawer-open']: showDrawer,
          ['transfer--customization-drawer-enabled']:
            isTransferCustomizationDrawerEnabled,
          ['transfer--customization-drawer-open']: customization.isDrawerOpen,
          ['transfer--drawer-close-animation']: isDrawerOpenForAnimation,
          ['transfer--tnc']: Browser.isMobile && (showTandc || showTandcExp),
        })}
        ref={transferWindowRef}
      >
        {showUploader && (
          <Uploader
            {...componentProps}
            transferWindowRef={transferWindowRef?.current}
          />
        )}
        {showDownloader && (
          <Downloader
            {...componentProps}
            transferWindowRef={transferWindowRef?.current}
          />
        )}
        {showTandc && <TermsConditions {...componentProps} />}
        {showTandcExp && <TermsConditionsExperiment {...componentProps} />}

        {isLayoutHidden && (
          <button
            className="transfer__block-click"
            onClick={showTransfer}
            tabIndex={0}
            onKeyPress={handleShowTransferKeyPress}
          />
        )}
        {showDrawer && (
          <TeamsDiscoveryDrawer
            title={I18n.t('drawer.first_viewing.title', {
              first_recipient_email: usersNotInTeam[0],
            })}
            textContent={I18n.t('drawer.second_viewing.subtitle', {
              user_plan: textContentParameter(),
            })}
            setIsDrawerOpen={setShowDrawer}
            setIsDrawerOpenForAnimation={setIsDrawerOpenForAnimation}
          />
        )}
        {isTransferCustomizationDrawerEnabled && (
          <TransferCustomizationDrawer />
        )}
      </div>
    );
  };

const WrappedTransferWindow = forwardRef(TransferWindow);

export { WrappedTransferWindow as TransferWindow };
